import { FormEvent, useEffect, useState } from 'react';
import { Dialog } from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Button } from '@/components/ui/Button';
import { api, Item } from '@/lib/api';
import { useToast } from '@/components/ui/Toast';

type Mode = { kind: 'create'; categoryId: number } | { kind: 'edit'; item: Item };

export function ItemEditDialog({
  open,
  onOpenChange,
  mode,
  onSaved,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  mode: Mode | null;
  onSaved: () => void;
}) {
  const [title, setTitle] = useState('');
  const [thumbnail, setThumbnail] = useState('');
  const [link, setLink] = useState('');
  const [isActive, setIsActive] = useState(true);
  const [loading, setLoading] = useState(false);
  const { push } = useToast();

  useEffect(() => {
    if (!mode || mode.kind === 'create') {
      setTitle('');
      setThumbnail('');
      setLink('');
      setIsActive(true);
      return;
    }
    setTitle(mode.item.title);
    setThumbnail(mode.item.thumbnail_url ?? '');
    setLink(mode.item.link_url ?? '');
    setIsActive(mode.item.is_active === 1);
  }, [mode]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!mode) return;
    setLoading(true);
    try {
      const payload = {
        title: title.trim(),
        thumbnail_url: thumbnail.trim() || null,
        link_url: link.trim() || null,
        is_active: isActive ? 1 : 0,
      };
      if (mode.kind === 'create') {
        await api.createItem({ category_id: mode.categoryId, ...payload });
        push('아이템 생성됨', 'success');
      } else {
        await api.updateItem(mode.item.id, payload);
        push('아이템 수정됨', 'success');
      }
      onOpenChange(false);
      onSaved();
    } catch (err) {
      push(err instanceof Error ? err.message : '저장 실패', 'error');
    } finally {
      setLoading(false);
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={mode?.kind === 'edit' ? '아이템 편집' : '새 아이템'}
      maxWidth="max-w-xl"
    >
      <form onSubmit={onSubmit} className="space-y-4">
        <div>
          <label className="mb-1 block text-xs font-medium">제목</label>
          <Input required autoFocus value={title} onChange={(e) => setTitle(e.target.value)} />
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">썸네일 URL</label>
          <Input
            type="url"
            placeholder="https://..."
            value={thumbnail}
            onChange={(e) => setThumbnail(e.target.value)}
          />
          {thumbnail && (
            <div className="mt-2 flex items-center gap-3 rounded-md border bg-muted/30 p-2">
              <div
                className="h-16 w-16 rounded border bg-contain bg-center bg-no-repeat"
                style={{ backgroundImage: `url('${thumbnail}')` }}
              />
              <span className="truncate text-xs text-muted-foreground">{thumbnail}</span>
            </div>
          )}
        </div>
        <div>
          <label className="mb-1 block text-xs font-medium">링크 URL</label>
          <Input type="url" placeholder="https://..." value={link} onChange={(e) => setLink(e.target.value)} />
        </div>
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
          활성 (위젯에 노출)
        </label>
        <div className="flex justify-end gap-2 pt-2">
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            취소
          </Button>
          <Button type="submit" disabled={loading}>
            {loading ? '저장 중…' : '저장'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
